export default (config = {}) => ({
    activeFilters: {},
    collectionMode: config.collectionMode || 'filter',
    currentSort: { key: 'popularity', value: 'ascending' },
    filterIsVisible: false,
    itemData: [],
    visibleCount: 0,

    init() {
        const grid = this.$el.querySelector('.js-grid');
        if (!grid) return;

        const itemEls = [...grid.querySelectorAll('.js-grid-item')];

        this.itemData = itemEls.map(el => ({
            el,
            categories: (el.dataset.voucherCategory || '').split(','),
            offer: el.dataset.voucherOffer || '',
            order: parseInt(el.dataset.voucherOrder) || 0,
            price: parseInt(el.dataset.voucherPrice) || 0,
            tags: (el.dataset.voucherTags || '').split(','),
            text: (el.querySelector('.content-preview')?.textContent || '').toLowerCase(),
        }));

        this.buildPriceBands();
        this.applyPresets();
        this.applyFilterAndSort();

        this.$watch('$store.filters.category', (value) => {
            const current = this.activeFilters.category?.value;

            if (value === '*' && !current) return;
            if (current === value) return;

            if (value === '*' || value === '') {
                delete this.activeFilters.category;
            } else {
                const select = this.$root.querySelector('select[data-filter="category"]');
                const option = select?.querySelector(`option[value="${CSS.escape(value)}"]`);

                if (this.collectionMode === 'navigate') {
                    const url = option?.dataset.filterUrl;

                    if (url) {
                        window.location = url;
                    }

                    return;
                }

                this.activeFilters.category = {
                    method: option?.dataset.filterMethod || 'category',
                    value,
                };
            }

            this.scrollToTop();
            this.applyFilterAndSort();
        });
    },

    setFilter(event) {
        const option = event.target.selectedOptions[0];
        const group = option?.dataset.filterGroup;

        if (this.collectionMode === 'navigate' && group === 'category') {
            const url = option?.dataset.filterUrl;

            if (url) {
                window.location = url;
            }

            return;
        }

        const method = option?.dataset.filterMethod;
        const value = event.target.value;

        if (value === '*' || value === '') {
            delete this.activeFilters[group];
        } else {
            this.activeFilters[group] = { method, value };
        }

        if (group === 'category') {
            this.$store.filters.category = value;
        }

        this.scrollToTop();
        this.applyFilterAndSort();
    },

    setSearchFilter(value) {
        if (value) {
            this.activeFilters.search = { method: 'search', value };
        } else {
            delete this.activeFilters.search;
        }
        this.applyFilterAndSort();
    },

    clearFilters() {
        this.activeFilters = {};
        this.filterIsVisible = false;
        this.$store.filters.category = '*';

        this.$root.querySelectorAll('select[data-filter]').forEach(select => {
            select.value = '*';
        });

        const searchInput = this.$root.querySelector('input[data-filter]');
        if (searchInput) {
            searchInput.value = '';
        }

        this.applyFilterAndSort();
    },

    isItemVisible(item) {
        for (const filter of Object.values(this.activeFilters)) {
            if (!this.filterMethods[filter.method](item, filter)) {
                return false;
            }
        }
        return true;
    },

    filterMethods: {
        category(item, filter) {
            return item.categories.includes(filter.value);
        },
        maxPrice(item, filter) {
            return item.price <= parseInt(filter.value);
        },
        offer(item, filter) {
            return parseInt(item.offer) === parseInt(filter.value);
        },
        search(item, filter) {
            return item.text.includes(filter.value.toLowerCase());
        },
        tag(item, filter) {
            return item.tags.some(t => parseInt(t) === parseInt(filter.value));
        },
    },

    setSort(key, value) {
        this.currentSort = { key, value };
        this.scrollToTop();
        this.applyFilterAndSort();
    },

    applyFilterAndSort() {
        const sorted = [...this.itemData].sort((a, b) => {
            const asc = this.currentSort.value === 'ascending';
            const aVal = this.currentSort.key === 'price' ? a.price : a.order;
            const bVal = this.currentSort.key === 'price' ? b.price : b.order;
            return asc ? aVal - bVal : bVal - aVal;
        });

        let count = 0;
        sorted.forEach((item, index) => {
            const visible = this.isItemVisible(item);
            item.el.style.display = visible ? '' : 'none';
            item.el.style.order = index;
            if (visible) count++;
        });

        this.visibleCount = count;

        const grid = this.$el.querySelector('.js-grid');
        if (grid) {
            grid.setAttribute('data-voucher-count', count);
        }

        window.dispatchEvent(new Event('images.gifted'));
    },

    buildPriceBands() {
        const prices = [...new Set(this.itemData.map(i => i.price))].sort((a, b) => a - b);
        if (prices.length < 2) return;

        const currencySymbol = document.body.dataset.currency || '';
        const bands = [0.2, 0.4, 0.6, 0.8]
            .map(step => this.nearestTen(prices[Math.min(Math.ceil(prices.length * step), prices.length - 1)]))
            .filter(value => value > 0)
            .filter((v, i, a) => a.indexOf(v) === i);

        const priceSelect = this.$el.querySelector('select[data-filter="price"]');
        if (!priceSelect) return;

        bands.forEach(pence => {
            const pounds = Math.ceil(pence / 100);
            const option = document.createElement('option');
            option.dataset.filterGroup = 'price';
            option.dataset.filterMethod = 'maxPrice';
            option.dataset.filterValue = pence;
            option.value = pence;
            option.textContent = `Up to ${currencySymbol}${pounds}`;
            priceSelect.appendChild(option);
        });
    },

    nearestTen(value) {
        return Math.round(value / 1000) * 1000;
    },

    applyPresets() {
        const params = Object.fromEntries(new URLSearchParams(window.location.search));
        const price = parseInt(params.price) * 100;
        const category = params.category;

        // Only preset a category the filter UI can actually express. The regions
        // layout hides the category select and filters server-side instead, where
        // ?category= carries a category id rather than the slug this matches on.
        const option = category
            ? this.$root.querySelector(`select[data-filter="category"] option[value="${CSS.escape(category)}"]`)
            : null;

        if (option) {
            this.activeFilters.category = { method: 'category', value: category };
            this.$store.filters.category = category;
        }
        if (price && !isNaN(price)) {
            this.activeFilters.price = { method: 'maxPrice', value: price };
        }
    },

    scrollToTop() {
        const hero = document.querySelector('.hero');
        if (hero) {
            window.scrollTo({ top: hero.offsetHeight, behavior: 'smooth' });
        }
    },
});
